import axios, { AxiosResponse } from 'axios'
import { env } from '@js/utils/env'

import gql from '@js/api/utils/gql'
import getStoreName from '@js/api/utils/getStoreName'
import getBearerToken from '@js/api/utils/getBearerToken'

interface ApplyCouponToRegistrationPayload {
  cartId: string
  couponCode: string
  registrationId: number
}

interface ApplyCouponToRegistrationResponse {
  data: {
    wceiApplyCouponToRegistration: {
      cart: {
        applied_coupons: { code: string }[]
        prices: {
          discounts?: {
            label: string
            amount: {
              value: number
              currency: string
            }
          }[]
          subtotal_excluding_tax: {
            value: number
            currency: string
          }
          subtotal_with_discount_excluding_tax: {
            value: number
            currency: string
          }
          applied_taxes?: {
            amount: {
              value: number
              currency: string
            }
          }[]
          grand_total: {
            value: number
            currency: string
          }
        }
      }
    } | null
  }
  errors?: [{ message: string }]
}

export const applyCouponToRegistrationMutation = gql`
  mutation(
    $cartId: String!,
    $couponCode: String!,
    $registrationId: Int!
  ) {
    wceiApplyCouponToRegistration(
      input: {
        cart_id: $cartId,
        coupon_code: $couponCode,
        registration_id: $registrationId
      }
    ) {
      cart {
        applied_coupons {
          code
        }
        prices {
          discounts {
            label
            amount {
              value
              currency
            }
          }
          subtotal_excluding_tax {
            value
            currency
          }
          subtotal_with_discount_excluding_tax {
            value
            currency
          }
          applied_taxes {
            amount {
              value
              currency
            }
          }
          grand_total {
            value
            currency
          }
        }
      }
    }
  }
`

/**
 * @throws {string}
 *
 * Will throw one error message; Magento only returns one at a time, rather than an array of all errors.
 */
export const applyCouponToRegistration = async (
  payload: ApplyCouponToRegistrationPayload
): Promise<ApplyCouponToRegistrationResponse['data']['wceiApplyCouponToRegistration'] | null> => {
  const bearerToken = getBearerToken()

  const { data }: AxiosResponse<ApplyCouponToRegistrationResponse> = await axios({
    method: 'POST',
    url: env.magento.GRAPHQL_URL,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${bearerToken}`,
      Store: getStoreName(),
    },
    data: {
      query: applyCouponToRegistrationMutation,
      variables: {
        cartId: payload.cartId,
        couponCode: payload.couponCode,
        registrationId: payload.registrationId,
      },
    },
  })

  if (data.errors) {
    throw data.errors[0].message
  }

  return data.data.wceiApplyCouponToRegistration
}
